import { useCallback, useEffect, useRef, useState } from 'react';
import { getIdentityHeaders } from './api';

const API_BASE = import.meta.env.VITE_API_URL || '';

export type StreamStatus = 'connected' | 'reconnecting' | 'disconnected';

export interface StreamEvent {
  id: string;
  type: string;
  data: Record<string, unknown>;
  timestamp: string;
}

interface UseEventStreamOptions {
  /** Max reconnection attempts before giving up */
  maxRetries?: number;
  /** Initial reconnection delay in ms (doubles with each attempt) */
  baseDelay?: number;
  /** When false, the hook will not attempt to connect (wait for auth) */
  enabled?: boolean;
}

/**
 * SSE hook — connects to /api/operator/events/stream via fetch ReadableStream.
 * Uses fetch instead of native EventSource to support custom identity headers.
 * Auto-reconnects with exponential backoff and Last-Event-ID support.
 */
export function useEventStream(options: UseEventStreamOptions = {}) {
  const { maxRetries = 5, baseDelay = 2000, enabled = true } = options;
  const [status, setStatus] = useState<StreamStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<StreamEvent | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const retriesRef = useRef(0);
  const lastEventIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const listenersRef = useRef<Map<string, Set<(event: StreamEvent) => void>>>(new Map());
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const emit = useCallback((event: StreamEvent) => {
    setLastEvent(event);
    const typeListeners = listenersRef.current.get(event.type);
    if (typeListeners) {
      typeListeners.forEach((cb) => cb(event));
    }
    // Also emit to wildcard listeners
    const allListeners = listenersRef.current.get('*');
    if (allListeners) {
      allListeners.forEach((cb) => cb(event));
    }
  }, []);

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Build headers — use the same identity headers as api.ts
    // plus credentials: include for session cookies
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      ...getIdentityHeaders(),
    };

    if (lastEventIdRef.current) {
      headers['Last-Event-ID'] = lastEventIdRef.current;
    }

    try {
      const res = await fetch(`${API_BASE}/api/operator/events/stream`, {
        headers,
        credentials: 'include',
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        // 401/403 = auth/permission issue — don't retry, it won't resolve
        if (res.status === 401 || res.status === 403) {
          setStatus('disconnected');
          return;
        }
        throw new Error(`SSE connection failed: ${res.status}`);
      }

      if (!mountedRef.current) return;
      setStatus('connected');
      retriesRef.current = 0;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (mountedRef.current) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = 'message';
        let eventData = '';
        let eventId = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            eventData += (eventData ? '\n' : '') + line.slice(5).trim();
          } else if (line.startsWith('id:')) {
            eventId = line.slice(3).trim();
          } else if (line === '') {
            // End of event block — dispatch
            if (eventData) {
              if (eventId) lastEventIdRef.current = eventId;
              if (eventType !== 'heartbeat') {
                try {
                  const parsed = JSON.parse(eventData);
                  emit({
                    id: eventId || crypto.randomUUID(),
                    type: eventType,
                    data: parsed,
                    timestamp: parsed.timestamp || new Date().toISOString(),
                  });
                } catch {
                  // Non-JSON data — emit as raw
                  emit({
                    id: eventId || crypto.randomUUID(),
                    type: eventType,
                    data: { raw: eventData },
                    timestamp: new Date().toISOString(),
                  });
                }
              }
            }
            // Reset for next event
            eventType = 'message';
            eventData = '';
            eventId = '';
          }
        }
      }

      // Stream ended normally (55s max duration) — reconnect silently
      // Keep status as 'connected' since this is expected behavior
      if (mountedRef.current) {
        retriesRef.current = 0;
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => connect(), 1000);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (mountedRef.current) {
        scheduleReconnect();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emit]);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    if (retriesRef.current >= maxRetries) {
      setStatus('disconnected');
      return;
    }
    setStatus('reconnecting');
    const delay = Math.min(baseDelay * Math.pow(2, retriesRef.current), 30000);
    const jitter = delay * 0.3 * Math.random();
    retriesRef.current += 1;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      connect();
    }, delay + jitter);
  }, [maxRetries, baseDelay, connect]);

  const subscribe = useCallback((eventType: string, callback: (event: StreamEvent) => void) => {
    if (!listenersRef.current.has(eventType)) {
      listenersRef.current.set(eventType, new Set());
    }
    listenersRef.current.get(eventType)!.add(callback);
    return () => {
      listenersRef.current.get(eventType)?.delete(callback);
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      retriesRef.current = 0;
      connect();
    } else {
      // Not ready — abort any existing connection
      abortRef.current?.abort();
      clearTimeout(reconnectTimerRef.current);
      setStatus('disconnected');
    }

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      clearTimeout(reconnectTimerRef.current);
    };
  }, [connect, enabled]);

  return { status, lastEvent, subscribe };
}
